import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Drawer,
  Modal,
  message,
  Tooltip,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  DownloadOutlined,
  SearchOutlined,
  EyeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { customGroupsAPI, accountsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import { generateCustomGroupReportPdf } from '../utils/customGroupReportPdf';

const { Title, Text } = Typography;
const { Search } = Input;

interface CustomGroup {
  _id: string;
  name: string;
  description: string;
  attachedGroups: Array<{
    _id: string;
    name: string;
    participantsCount: number;
    accountId?: {
      _id: string;
      accountName: string;
      phoneNumber: string;
    };
  }>;
  createdBy: {
    username: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface AnalyticsData {
  customGroup: {
    _id: string;
    name: string;
    description: string;
  };
  overview: {
    totalGroups: number;
    totalMembers: number;
    uniqueMembers: number;
    avgGroupSize: number;
  };
  groupsByAccount: Array<{
    accountId: string;
    accountName: string;
    phoneNumber: string;
    groupsCount: number;
    membersCount: number;
    groups: Array<{
      _id: string;
      name: string;
      participantsCount: number;
    }>;
  }>;
  groupsBySize: {
    '0-10': number;
    '11-25': number;
    '26-50': number;
    '51-100': number;
    '101-200': number;
    '201-500': number;
    '500+': number;
  };
  topGroups: Array<{
    _id: string;
    name: string;
    participantsCount: number;
    accountName: string;
  }>;
  allGroups: Array<{
    _id: string;
    name: string;
    description: string;
    participantsCount: number;
    accountId: any;
  }>;
}

const CustomGroups: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customGroups, setCustomGroups] = useState<CustomGroup[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewDrawerVisible, setViewDrawerVisible] = useState(false);
  const [viewingGroup, setViewingGroup] = useState<CustomGroup | null>(null);
  const [viewingGroupDetails, setViewingGroupDetails] = useState<any>(null);
  const [viewingGroupLoading, setViewingGroupLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [viewGroupFilter, setViewGroupFilter] = useState<string>('all');
  const [viewMemberSearch, setViewMemberSearch] = useState('');
  const [viewGroupFilterSearch, setViewGroupFilterSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customGroupsRes, accountsRes] = await Promise.all([
        customGroupsAPI.getAll(),
        accountsAPI.getAll(),
      ]);

      setCustomGroups(customGroupsRes.data);
      setAccounts(accountsRes.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch data');
      message.error(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: 'Delete Custom Group',
      content: 'Are you sure you want to delete this custom group?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await customGroupsAPI.delete(id);
          message.success('Custom group deleted successfully');
          await fetchData();
        } catch (err: any) {
          message.error(err.response?.data?.error || 'Failed to delete custom group');
        }
      },
    });
  };

  const handleViewAnalytics = (id: string) => {
    navigate(`/custom-groups/${id}/analytics`);
  };

  const handleDownloadReport = async (record: CustomGroup) => {
    const hide = message.loading('Preparing report...', 0);
    try {
      const response = await customGroupsAPI.getAnalytics(record._id);
      generateCustomGroupReportPdf(response.data);
      message.success(`Report downloaded: ${record.name}`);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to download report');
    } finally {
      hide();
    }
  };

  const handleViewGroup = async (group: CustomGroup) => {
    try {
      setViewingGroupLoading(true);
      setViewingGroup(group);
      const response = await customGroupsAPI.getById(group._id);
      setViewingGroupDetails(response.data);
      setViewGroupFilter('all');
      setViewMemberSearch('');
      setViewDrawerVisible(true);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to fetch group details');
    } finally {
      setViewingGroupLoading(false);
    }
  };

  const handleCloseViewDrawer = () => {
    setViewDrawerVisible(false);
    setViewingGroup(null);
    setViewingGroupDetails(null);
    setViewGroupFilter('all');
    setViewMemberSearch('');
    setViewGroupFilterSearch('');
  };

  const filteredGroupsForViewSelect = useMemo(() => {
    if (!viewingGroupDetails?.attachedGroups) return [];
    if (!viewGroupFilterSearch) return viewingGroupDetails.attachedGroups;
    
    const searchLower = viewGroupFilterSearch.toLowerCase();
    return viewingGroupDetails.attachedGroups.filter((group: any) =>
      group.name?.toLowerCase().includes(searchLower) ||
      group.accountId?.accountName?.toLowerCase().includes(searchLower)
    );
  }, [viewingGroupDetails, viewGroupFilterSearch]);


  const filteredGroups = useMemo(() => {
    return customGroups.filter((group) => {
      const matchesSearch =
        searchText === '' ||
        group.name.toLowerCase().includes(searchText.toLowerCase()) ||
        group.description.toLowerCase().includes(searchText.toLowerCase());

      return matchesSearch;
    });
  }, [customGroups, searchText]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
      sorter: (a: CustomGroup, b: CustomGroup) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text || 'No description'}>
          {text || 'No description'}
        </Tooltip>
      ),
    },
    {
      title: 'Attached Groups',
      key: 'attachedGroups',
      render: (_: any, record: CustomGroup) => (
        <Tag color="#0e4842">{record.attachedGroups.length} groups</Tag>
      ),
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'username'],
      key: 'createdBy',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CustomGroup) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewGroup(record)}
            />
          </Tooltip>
          <Tooltip title="View Analytics">
            <Button
              type="primary"
              icon={<BarChartOutlined />}
              onClick={() => handleViewAnalytics(record._id)}
            />
          </Tooltip>
          <Tooltip title="Download Report">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadReport(record)}
            />
          </Tooltip>
          {(user?.role === 'super_admin' || user?.role === 'admin') && (
            <>
              <Tooltip title="Edit">
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/custom-groups/${record._id}/edit`)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record._id)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  if (loading && customGroups.length === 0) {
    return <Loader fullScreen text="Loading custom groups..." />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
          <Title level={2} className="mb-0">Custom Groups Management</Title>
          <div className="flex items-center gap-2">
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                onClick={() => navigate('/custom-groups/create')}
              >
                Create Custom Group
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Total Custom Groups"
                value={customGroups.length}
                valueStyle={{ color: '#0e4842' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Total Attached Groups"
                value={customGroups.reduce((sum, g) => sum + g.attachedGroups.length, 0)}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Filtered Results"
                value={filteredGroups.length}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Search */}
        <Card className="mb-6">
          <Search
            placeholder="Search by name or description..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Card>

        {/* Custom Groups Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredGroups}
            rowKey="_id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} custom groups`,
            }}
            locale={{
              emptyText: (
                <div className="py-12 text-center">
                  <Text type="secondary">No custom groups found.</Text>
                  {(user?.role === 'super_admin' || user?.role === 'admin') && (
                    <div className="mt-4">
                      <Button type="primary" onClick={() => navigate('/custom-groups/create')}>
                        Create Your First Custom Group
                      </Button>
                    </div>
                  )}
                </div>
              ),
            }}
          />
        </Card>

        {/* View Custom Group Details Drawer */}
        <Drawer
          title={
            <div>
              <Title level={4} className="mb-0">
                {viewingGroup?.name || 'Custom Group Details'}
              </Title>
            </div>
          }
          placement="right"
          width={900}
          open={viewDrawerVisible}
          onClose={handleCloseViewDrawer}
          maskClosable={false}
        >
          {viewingGroupLoading ? (
            <Loader text="Loading group details..." />
          ) : viewingGroupDetails ? (
            <div className="space-y-6">
              {/* Custom Group Information */}
              <Card>
                <Title level={5}>Custom Group Information</Title>
                <div className="space-y-3 mt-4">
                  <div>
                    <Text strong>Name: </Text>
                    <Text>{viewingGroupDetails.name}</Text>
                  </div>
                  {viewingGroupDetails.description && (
                    <div>
                      <Text strong>Description: </Text>
                      <Text>{viewingGroupDetails.description}</Text>
                    </div>
                  )}
                  <div>
                    <Text strong>Total Groups: </Text>
                    <Tag color="#0e4842">
                      {viewingGroupDetails.attachedGroups?.length || 0} groups
                    </Tag>
                  </div>
                  <div>
                    <Text strong>Total Members: </Text>
                    <Tag color="#1890ff">
                      {viewingGroupDetails.attachedGroups?.reduce(
                        (sum: number, g: any) => sum + (g.participantsCount || 0),
                        0
                      )}{' '}
                      members
                    </Tag>
                  </div>
                  <div>
                    <Text strong>Created By: </Text>
                    <Text>
                      {viewingGroupDetails.createdBy?.username || 'Unknown'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>Created At: </Text>
                    <Text>
                      {new Date(viewingGroupDetails.createdAt).toLocaleString()}
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Filter and Search */}
              <Card>
                <div className="flex gap-4 mb-4">
                  <Select
                    placeholder="Filter by Group"
                    style={{ width: 300 }}
                    size="large"
                    value={viewGroupFilter}
                    onChange={setViewGroupFilter}
                    showSearch
                    filterOption={false}
                    onSearch={setViewGroupFilterSearch}
                    optionLabelProp="label"
                    dropdownRender={(menu: React.ReactNode) => (
                      <>
                        <div style={{ padding: '8px' }}>
                          <Input
                            placeholder="Search groups by name or account..."
                            value={viewGroupFilterSearch}
                            onChange={(e) => setViewGroupFilterSearch(e.target.value)}
                            allowClear
                            prefix={<SearchOutlined />}
                          />
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        {menu}
                      </>
                    )}
                    notFoundContent={
                      !viewingGroupDetails.attachedGroups || viewingGroupDetails.attachedGroups.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center' }}>
                          <Text type="secondary">No groups available</Text>
                        </div>
                      ) : viewGroupFilterSearch && filteredGroupsForViewSelect.length === 0 ? (
                        <div style={{ padding: '12px', textAlign: 'center' }}>
                          <Text type="secondary">
                            No groups found matching "{viewGroupFilterSearch}"
                          </Text>
                        </div>
                      ) : null
                    }
                  >
                    <Select.Option value="all" label="All Groups">
                      All Groups
                    </Select.Option>
                    {filteredGroupsForViewSelect.map((group: any) => (
                      <Select.Option key={group._id} value={group._id} label={group.name}>
                        <div>
                          <Text strong>{group.name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {group.accountId?.accountName || 'Unknown'} • {group.participantsCount || 0} members
                          </Text>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                  <Search
                    placeholder="Search members by name, phone, or group..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    style={{ flex: 1 }}
                    value={viewMemberSearch}
                    onChange={(e) => setViewMemberSearch(e.target.value)}
                  />
                </div>
              </Card>

              {/* Groups List */}
              <Card>
                <Title level={5}>Groups</Title>
                <Table
                  dataSource={
                    viewingGroupDetails.attachedGroups?.filter((group: any) => {
                      if (viewGroupFilter === 'all') return true;
                      return group._id === viewGroupFilter;
                    }) || []
                  }
                  rowKey="_id"
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Group Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: (text: string) => <Text strong>{text}</Text>,
                    },
                    {
                      title: 'Account',
                      key: 'account',
                      render: (_: any, record: any) =>
                        record.accountId?.accountName || 'Unknown',
                    },
                    {
                      title: 'Members',
                      dataIndex: 'participantsCount',
                      key: 'participantsCount',
                      render: (count: number) => (
                        <Tag color="#0e4842">{count || 0}</Tag>
                      ),
                    },
                  ]}
                />
              </Card>

              {/* Members List */}
              <Card>
                <Title level={5}>Members</Title>
                <Table
                  dataSource={(() => {
                    let members: any[] = [];
                    const filteredGroups =
                      viewingGroupDetails.attachedGroups?.filter((group: any) => {
                        if (viewGroupFilter === 'all') return true;
                        return group._id === viewGroupFilter;
                      }) || [];

                    filteredGroups.forEach((group: any) => {
                      if (group.participants && Array.isArray(group.participants)) {
                        group.participants.forEach((member: any) => {
                          members.push({
                            ...member,
                            groupId: group._id,
                            groupName: group.name,
                            accountName: group.accountId?.accountName || 'Unknown',
                          });
                        });
                      }
                    });

                    // Filter by search text
                    if (viewMemberSearch) {
                      const searchLower = viewMemberSearch.toLowerCase();
                      members = members.filter(
                        (member) =>
                          member.name?.toLowerCase().includes(searchLower) ||
                          member.phoneNumber?.toLowerCase().includes(searchLower) ||
                          member.id?.toLowerCase().includes(searchLower) ||
                          member.groupName?.toLowerCase().includes(searchLower) ||
                          member.accountName?.toLowerCase().includes(searchLower)
                      );
                    }

                    return members;
                  })()}
                  rowKey={(record) => `${record.groupId}-${record.id || record.phoneNumber}`}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} members`,
                  }}
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      render: (text: string, record: any) => (
                        <div>
                          <Text strong>{text || 'Unknown'}</Text>
                          {record.isSuperAdmin && (
                            <Tag color="red" style={{ marginLeft: 8 }}>Super Admin</Tag>
                          )}
                          {record.isAdmin && !record.isSuperAdmin && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>Admin</Tag>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: 'Phone Number',
                      dataIndex: 'phoneNumber',
                      key: 'phoneNumber',
                      render: (text: string, record: any) => text || record.id || 'N/A',
                    },
                    {
                      title: 'Group',
                      dataIndex: 'groupName',
                      key: 'groupName',
                      render: (text: string, record: any) => (
                        <div>
                          <Text strong>{text}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.accountName}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      title: 'Role',
                      key: 'role',
                      render: (_: any, record: any) => {
                        if (record.isSuperAdmin) {
                          return <Tag color="red">Super Admin</Tag>;
                        }
                        if (record.isAdmin) {
                          return <Tag color="blue">Admin</Tag>;
                        }
                        return <Tag>Member</Tag>;
                      },
                    },
                  ]}
                />
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <Text type="secondary">No details available</Text>
            </div>
          )}
        </Drawer>

      </div>
    </div>
  );
};

export default CustomGroups;
